import { tokens } from '@/styles/tokens/semantics';
import { alpha, styled } from '@mui/material/styles';

export type StyledPhoneInputSurface = 'default' | 'grey120' | 'outlined';

export const PhoneInputContainer = styled('div', {
  shouldForwardProp: (prop) =>
    !['error', 'label', 'highlighted', 'readOnly', 'surface'].includes(String(prop)),
})<{
  error?: boolean;
  label?: boolean;
  highlighted?: boolean;
  readOnly?: boolean;
  surface?: StyledPhoneInputSurface;
}>(({ error, highlighted, readOnly, surface = 'default', theme }) => {
  const isGrey120 = surface === 'grey120';
  const isOutlined = surface === 'outlined';
  const isReadOnly = Boolean(readOnly);

  return {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    position: 'relative',
    width: '100%',
    '--PhoneInputCountryFlag-height': '0.75em',

    '& .phone-input-root': {
      padding: 0,
      fontSize: isOutlined ? '0.875rem' : '16px',
      margin: 0,
      border:
        isGrey120 || isReadOnly
          ? '1px solid transparent'
          : `1px solid ${
              isOutlined ? theme.palette.grey[90] : alpha(theme.palette.primary.darker, 0.2)
            }`,
      borderRadius: '8px',
      overflow: 'hidden',
      gap: 0,
      ...((isGrey120 || isReadOnly) && {
        backgroundColor: theme.palette.grey[120],
      }),

      '&:hover': {
        borderColor:
          isGrey120 || isReadOnly
            ? 'transparent'
            : isOutlined
              ? theme.palette.grey[90]
              : theme.palette.primary.main,
        ...((isOutlined || isReadOnly) && {
          backgroundColor: theme.palette.grey[120],
        }),
      },

      '& input': {
        padding: '7px 14px 7px 5px',
        border: 0,
        fontVariantNumeric: 'lining-nums proportional-nums',
        color: theme.palette.grey[0],
        fontWeight: 500,
        fontSize: 15,
        backgroundColor:
          isGrey120 || isReadOnly
            ? theme.palette.grey[120]
            : highlighted
              ? alpha(theme.palette.primary.lighter, 0.1)
              : undefined,
      },

      '& .PhoneInputCountry': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.5rem',
        margin: 0,
        width: 40,
      },

      '&.PhoneInput--focus': {
        borderColor: isReadOnly ? 'transparent' : theme.palette.primary.main,
        boxShadow: isReadOnly ? 'none' : `0 0 0 1px ${tokens.primary.lighter}`,
        backgroundColor: isReadOnly ? theme.palette.grey[120] : 'transparent',

        '& input, & .country-select, & .PhoneInputCountry': {
          backgroundColor: isReadOnly ? theme.palette.grey[120] : 'transparent',
        },
      },

      '&:focus': {
        outline: 'none',
      },

      ...(error && {
        borderColor: theme.palette.error.main,
        backgroundColor: alpha(theme.palette.error.main, 0.1),

        '& input': {
          padding: isOutlined ? '9.5px 14px' : '9px 14px',
          border: 0,
          backgroundColor: 'transparent',
        },
      }),

      '&.PhoneInput--disabled': {
        borderColor: 'transparent',
        backgroundColor: theme.palette.grey[90],

        '& input, & .country-select, & .PhoneInputCountry': {
          backgroundColor: 'transparent',
        },
      },
    },

    '& .country-select': {
      border: 'none',
    },
  };
});
